import { useEffect, useState } from "react";
import { toast } from "sonner";
import { House, DownloadSimple } from "@phosphor-icons/react";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const STAGES = ["New", "Contacted", "Qualified", "Proposal", "Won", "Lost"];
const COMMISSION_STATUSES = ["pending", "approved", "rejected", "paid"];
const SOURCE_LABEL = { trust: "Trust & Asset Protection", funding: "Business Funding" };
const navItems = [{ to: "/admin/affiliate", label: "Sales Reps", icon: <House size={18} weight="duotone" />, testId: "nav-admin-affiliate" }];

export default function AdminAffiliatePage() {
  const confirmEdit = useEditConfirm();
  const [records, setRecords] = useState([]);
  const [notesDraft, setNotesDraft] = useState({});
  const [amountDraft, setAmountDraft] = useState({});

  const fetchAll = async () => {
    const { data } = await api.get("/admin/affiliate");
    setRecords(data);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const updateStage = async (id, stage) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.name || "this lead"}'s stage`))) return;
    try {
      await api.patch(`/admin/leads/${id}`, { stage });
      toast.success("Stage updated");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateCommission = async (id, commission_status, currentAmount) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.name || "this lead"}'s commission status`))) return;
    try {
      const amount = amountDraft[id] !== undefined ? parseFloat(amountDraft[id]) : currentAmount;
      await api.patch(`/admin/leads/${id}`, { commission_status, commission_amount: amount });
      toast.success("Commission updated");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveAmount = async (id, currentAmount, currentStatus) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.name || "this lead"}'s commission amount`))) return;
    try {
      const amount = amountDraft[id] !== undefined ? parseFloat(amountDraft[id]) : currentAmount;
      await api.patch(`/admin/leads/${id}`, { commission_amount: amount, commission_status: currentStatus });
      toast.success("Commission amount saved");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveNotes = async (id, currentNotes) => {
    const r = records.find((x) => x.id === id);
    if (!(await confirmEdit(`${r?.name || "this lead"}'s notes`))) return;
    try {
      const value = notesDraft[id] !== undefined ? notesDraft[id] : currentNotes;
      await api.patch(`/admin/leads/${id}`, { admin_notes: value });
      toast.success("Notes saved");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const totalPaid = records.reduce((sum, r) => sum + (r.commission_status === "paid" ? r.commission_amount || 0 : 0), 0);
  const approvedCount = records.filter((r) => r.commission_status === "approved").length;

  const markAllPaid = async () => {
    if (!(await confirmEdit(`${approvedCount} approved commission(s) (mark as paid)`))) return;
    try {
      const { data } = await api.post("/admin/affiliate/mark-all-paid");
      toast.success(data.message);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/admin/affiliate/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "igc_affiliate_roster.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Sales Reps" moduleBackPath="/admin">
      <div data-testid="admin-affiliate-page">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight" title="Every client-submitted referral for Trust & Asset Protection or Business Funding — track each one's stage and commission owed.">Sales Reps</h1>
          <div className="flex items-center gap-3">
            {approvedCount > 0 && (
              <Button data-testid="affiliate-mark-all-paid-button" onClick={markAllPaid} title="Marks every commission currently sitting at Approved as fully Paid, in one click." className="bg-igc-gold hover:bg-igc-goldHover text-black">
                Mark All Approved as Paid ({approvedCount})
              </Button>
            )}
            <Button data-testid="affiliate-export-button" onClick={handleExport} variant="outline" title="Downloads a spreadsheet (CSV) of every affiliate referral shown below." className="border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black flex items-center gap-2">
              <DownloadSimple size={16} weight="duotone" /> Export CSV
            </Button>
          </div>
        </div>
        <p className="text-igc-muted text-sm mb-6">All client-submitted referrals for Trust & Asset Protection and Business Funding — 20% commission on closed deals.</p>
        <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-8 inline-block" data-testid="affiliate-total-paid">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-1" title="The running total of every commission that has actually been paid out to affiliates, across all referrals.">Total Commissions Paid</p>
          <p className="font-serif text-2xl text-igc-success">${totalPaid.toLocaleString()}</p>
        </div>

        {records.length === 0 && <p className="text-igc-muted text-sm">No affiliate referrals yet.</p>}
        <div className="space-y-4">
          {records.map((r) => (
            <div key={r.id} className="bg-igc-surface border border-igc-gold/15 p-6" data-testid={`affiliate-record-${r.id}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <p className="font-serif text-lg text-igc-cream">{r.name}</p>
                  <p className="text-igc-muted text-xs">{r.store_display_name} referred — {SOURCE_LABEL[r.source] || r.source} — {r.contact}</p>
                </div>
                <Select value={r.stage} onValueChange={(v) => updateStage(r.id, v)}>
                  <SelectTrigger data-testid={`affiliate-stage-select-${r.id}`} title="Where this referral is at in the sales process — New through Won or Lost." className="w-48 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-igc-muted text-xs mb-3">Est. deal value ${(r.deal_value || 0).toLocaleString()} — Updated {formatET(r.updated_at, "MMM d, yyyy h:mm a")}</p>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Input data-testid={`affiliate-amount-input-${r.id}`} type="number" placeholder="Commission $" title="The dollar amount owed to the affiliate for this referral." defaultValue={r.commission_amount || ""} onChange={(e) => setAmountDraft({ ...amountDraft, [r.id]: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40" />
                <Button data-testid={`affiliate-amount-save-${r.id}`} onClick={() => saveAmount(r.id, r.commission_amount, r.commission_status)} title="Saves the commission dollar amount you typed." className="bg-igc-gold hover:bg-igc-goldHover text-black">Save Amount</Button>
                <Select value={r.commission_status} onValueChange={(v) => updateCommission(r.id, v, r.commission_amount)}>
                  <SelectTrigger data-testid={`affiliate-commission-status-select-${r.id}`} title="Pending = not yet decided. Approved = will be paid. Rejected = won't be paid. Paid = already sent." className="w-40 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    {COMMISSION_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Textarea data-testid={`affiliate-notes-input-${r.id}`} defaultValue={r.admin_notes} onChange={(e) => setNotesDraft({ ...notesDraft, [r.id]: e.target.value })} placeholder="Internal notes (admin-only)..." title="Private notes about this referral, visible only to admins." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream mb-3 min-h-16" />
              <Button data-testid={`affiliate-notes-save-${r.id}`} onClick={() => saveNotes(r.id, r.admin_notes)} title="Saves the note you just typed." className="bg-transparent border border-igc-gold text-igc-goldText hover:bg-igc-gold hover:text-black">Save Notes</Button>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
